import {
  ContentNodeUtils,
  DataTypeLocator,
  ListContentNode,
  ObjectContentNode,
  QueryNode,
  QueryNodeUtils,
} from '@helo/cms'
import { pipe } from '@helo/utils'
import { IServerSideGetRowsParams } from 'ag-grid-enterprise'

import { AbortError } from '../../services/apolloClient'
import { useAppStore } from '../../store/appStore/appStore'

/**
 * Helper to check if an error is an abort error.
 * Abort errors occur when a query is cancelled because a newer query
 * with the same ID was initiated. These are intentional cancellations,
 * not actual failures.
 *
 * Apollo Client may wrap abort errors in different ways:
 * - Direct DOMException with name 'AbortError' from fetch
 * - Our custom AbortError class
 * - Apollo's ApolloError which may contain the abort error in networkError
 * - Error with message containing 'aborted'
 */
const isAbortError = (error: unknown): boolean => {
  if (!error) return false

  // Check for our custom AbortError
  if (error instanceof AbortError) return true

  // Check for DOM's AbortError (from fetch)
  if (error instanceof Error && error.name === 'AbortError') return true
  if (
    error &&
    typeof error === 'object' &&
    'apolloState' in error &&
    (error.apolloState as any)?.error?.networkError instanceof AbortError
  )
    return true

  // Check for ApolloError wrapping an abort error
  if (error && typeof error === 'object') {
    const apolloError = error as { networkError?: Error; message?: string }

    // Check networkError property (Apollo's way of wrapping network errors)
    if (apolloError.networkError) {
      if (apolloError.networkError.name === 'AbortError') return true
      if (apolloError.networkError instanceof AbortError) return true
    }

    // Check if the message indicates an abort
    if (
      apolloError.message &&
      (apolloError.message.includes('aborted') ||
        apolloError.message.includes('The user aborted a request'))
    ) {
      return true
    }
  }

  return false
}

const logDataSourceError = (
  message: string,
  context: Record<string, unknown>,
  error?: unknown,
) => {
  if (error !== undefined) {
    console.error(`[DataSource] ${message}`, { ...context, error })
    return
  }

  console.error(`[DataSource] ${message}`, context)
}

const failDataSourceRequest = (
  params: Pick<IServerSideGetRowsParams, 'api' | 'fail'>,
  setLoading: (loading: boolean) => void,
) => {
  setLoading(false)
  setTimeout(() => {
    if (params.api.isDestroyed()) return
    params.api.showNoRowsOverlay()
  }, 1)
  params.fail()
}

export const universalTableQueryDataSource = (
  queryId: string,
  setLoading: (loading: boolean) => void,
  setTotalCount: (totalCount: number) => void,
  connectionLocator: DataTypeLocator,
) => {
  return {
    getRows: async (params: IServerSideGetRowsParams) => {
      const { api, success, fail, request } = params
      const { startRow, endRow } = request
      try {
        if (api.isDestroyed()) return

        const currentQueryNode = useAppStore
          .getState()
          .queryStore.getQuery(queryId)
        if (!currentQueryNode) {
          logDataSourceError('Query node not found', {
            queryId,
            startRow,
            endRow,
          })
          failDataSourceRequest(params, setLoading)
          return
        }

        const latestQueryNode =
          useAppStore.getState().queryStore.getQuery(queryId) ||
          currentQueryNode

        const currentVariables =
          QueryNodeUtils.getMappedVariablesAsObject()(latestQueryNode)

        const first = (endRow ?? 150) - (startRow ?? 0)

        // Only update pagination variables - orderBy is already set by onSortChanged
        const variablesToUpdate: Record<string, unknown> = {
          ...currentVariables,
          first,
          skip: startRow ?? 0,
        }

        // ⚠️ APOLLO CLIENT NETWORK CALL TRIGGER POINT: updateVariables may trigger a network request
        // This is called from datasources.ts getRows() - if variables changed, it will call refetch or startQueryAndSubscribe
        const updatedQueryNode = await useAppStore
          .getState()
          .queryStore.updateVariables(queryId, variablesToUpdate)
        if (api.isDestroyed()) return

        // Determine which query node to use for data extraction
        let responseQueryNode: QueryNode | undefined

        // If variables haven't changed, use existing data instead of re-fetching.
        // AG Grid still needs the data after a purge/refresh, but we don't need to
        // make a network call if the variables are the same.
        if (!updatedQueryNode.variablesHaveChanged) {
          // Get the query and wait for result if there's a query in flight
          // This ensures we get complete data even if a query is currently loading
          responseQueryNode = await useAppStore
            .getState()
            .queryStore.getQueryAndWaitForResult(queryId)
        } else {
          // Variables changed - fetch new data
          // ⚠️ APOLLO CLIENT NETWORK CALL TRIGGER POINT: refetchQuery triggers a network request
          // This is called from datasources.ts getRows() when variables changed

          responseQueryNode = await useAppStore
            .getState()
            .queryStore.refetchQuery(queryId)
        }
        if (!responseQueryNode) {
          logDataSourceError('No query node found after fetch', {
            queryId,
            startRow,
            endRow,
          })
          failDataSourceRequest(params, setLoading)
          return
        }

        // Verify the query node has valid content data before proceeding
        if (!responseQueryNode.queryContentNode) {
          logDataSourceError('Query node has no content data', {
            queryId,
            startRow,
            endRow,
          })
          failDataSourceRequest(params, setLoading)
          return
        }

        /**
         * updateQuery is a promise, and it's possible for the component to unmount
         * before the response is received. This line checks for that condition
         * and returns before setting any state.
         */
        if (api.isDestroyed()) return

        const connectionContentNode = pipe(
          responseQueryNode?.queryContentNode as any,
          ContentNodeUtils.findContentNode(connectionLocator),
        ) as ObjectContentNode | null

        if (!connectionContentNode) {
          logDataSourceError('Connection content node not found', {
            queryId,
            connectionLocator,
            startRow,
            endRow,
          })
          failDataSourceRequest(params, setLoading)
          return
        }

        const itemsListContentNode = pipe(
          connectionContentNode,
          ContentNodeUtils.findContentNode([
            ['key', 'items'],
            ['typeName', 'List'],
          ]),
        ) as ListContentNode | null

        if (!itemsListContentNode) {
          logDataSourceError('Items list content node not found', {
            queryId,
            connectionLocator,
            startRow,
            endRow,
          })
          failDataSourceRequest(params, setLoading)
          return
        }

        const payload = connectionContentNode.data as any
        const items = itemsListContentNode.items ?? []
        const totalCount = payload?.totalCount ?? 0

        setTotalCount(totalCount)

        success({
          rowData: items,
          rowCount: totalCount,
        })
        setLoading(false)

        // Explicitly manage overlay state - AG Grid doesn't auto-hide overlays
        if (items.length === 0) {
          setTimeout(() => {
            if (api.isDestroyed()) return
            api.showNoRowsOverlay()
          }, 1)
        } else {
          // Hide overlay when rows are returned (e.g., after clearing a filter)
          setTimeout(() => {
            if (api.isDestroyed()) return
            api.hideOverlay()
          }, 1)
        }
      } catch (e) {
        // Abort errors are intentional cancellations - a newer query is in progress.
        if (isAbortError(e)) {
          console.debug(
            `[DataSource] Query ${queryId} was aborted (rows ${startRow}-${endRow}). A newer query is in progress.`,
          )
          return
        }

        logDataSourceError(
          'Failed to load table rows',
          {
            queryId,
            startRow,
            endRow,
          },
          e,
        )
        failDataSourceRequest(params, setLoading)
      }
    },
  }
}
